import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconShieldCheck,
} from '@tabler/icons-react';
import { RegistrationLayout } from './RegistrationLayout';
import { getLegalDocuments, RegistrationError, startRegistration } from './api';
import { useLocaleStore } from '../../shared/i18n/localeStore';

interface FormState {
  businessName: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  warehouseRange: '1' | '2-5' | '6-20' | '20+' | '';
  acceptedIds: Set<string>;
}

const WAREHOUSE_RANGE_MAP: Record<Exclude<FormState['warehouseRange'], ''>, number> = {
  '1': 1,
  '2-5': 5,
  '6-20': 20,
  '20+': 50,
};

const initialState: FormState = {
  businessName: '',
  contactFirstName: '',
  contactLastName: '',
  contactEmail: '',
  contactPhone: '',
  warehouseRange: '',
  acceptedIds: new Set<string>(),
};

function legalDocLabelKey(documentType: string): string {
  switch (documentType.toLowerCase()) {
    case 'tos':
    case 'terms':
    case 'terms_of_service':
      return 'registration.start.tos' as const;
    case 'privacy':
    case 'privacy_policy':
      return 'registration.start.privacy' as const;
    default:
      return documentType;
  }
}

function readableLegalDocTitle(documentType: string, t: (k: string) => string): string {
  // Friendly names come from registration.start.legalDocs.{type}
  // (eula / privacy_policy / dpa / acceptable_use). i18next returns the key
  // unchanged when it's missing, so fall back to the raw type for any new type.
  const key = `registration.start.legalDocs.${documentType.toLowerCase()}`;
  const label = t(key);
  return label === key ? documentType : label;
}

export function StartRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = useLocaleStore((s) => s.locale);

  const legalDocsQuery = useQuery({
    queryKey: ['registration', 'legalDocuments'],
    queryFn: ({ signal }) => getLegalDocuments(signal),
    staleTime: 5 * 60 * 1000,
  });

  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      return startRegistration({
        businessName: form.businessName.trim(),
        contactEmail: form.contactEmail.trim(),
        contactFirstName: form.contactFirstName.trim(),
        contactLastName: form.contactLastName.trim(),
        contactPhone: form.contactPhone.trim() || null,
        expectedWarehouseCount:
          form.warehouseRange && WAREHOUSE_RANGE_MAP[form.warehouseRange] !== undefined
            ? WAREHOUSE_RANGE_MAP[form.warehouseRange]
            : null,
        acceptedLegalDocumentIds: Array.from(form.acceptedIds),
        preferredCulture: locale,
      });
    },
    onSuccess: (response) => {
      void navigate({
        to: '/register/sent',
        search: { email: response.contactEmail },
      });
    },
    onError: (err) => {
      if (err instanceof RegistrationError) {
        if (err.code === 'email_in_use') {
          setSubmitError(t('registration.start.errors.emailInUse'));
          return;
        }
        if (err.code === 'turnstile_failed') {
          setSubmitError(t('registration.start.errors.turnstileFailed'));
          return;
        }
      }
      setSubmitError(t('registration.start.errors.generic'));
    },
  });

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.businessName.trim()) next.businessName = t('registration.start.errors.businessNameRequired');
    if (!form.contactFirstName.trim()) next.firstName = t('registration.start.errors.firstNameRequired');
    if (!form.contactLastName.trim()) next.lastName = t('registration.start.errors.lastNameRequired');
    if (!form.contactEmail.trim()) {
      next.email = t('registration.start.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())) {
      next.email = t('registration.start.errors.emailInvalid');
    }
    if (
      legalDocsQuery.data &&
      legalDocsQuery.data.some((d) => !form.acceptedIds.has(d.legalDocumentId))
    ) {
      next.legal = t('registration.start.errors.legalDocsRequired');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;
    mutation.mutate();
  }

  function toggleDoc(id: string, checked: boolean) {
    setForm((prev) => {
      const next = new Set(prev.acceptedIds);
      if (checked) next.add(id); else next.delete(id);
      return { ...prev, acceptedIds: next };
    });
  }

  return (
    <RegistrationLayout step="step1Of3">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2}>{t('registration.start.title')}</Title>
          <Text size="sm" c="dimmed">{t('registration.start.subtitle')}</Text>
        </Stack>

        <form onSubmit={onSubmit} noValidate>
          <Stack gap="md">
            <TextInput
              label={t('registration.start.businessName')}
              placeholder={t('registration.start.businessNamePlaceholder')}
              value={form.businessName}
              onChange={(e) => setForm({ ...form, businessName: e.currentTarget.value })}
              error={errors.businessName}
              required
              autoComplete="organization"
            />

            <SimpleGrid cols={2} spacing="sm">
              <TextInput
                label={t('registration.start.firstName')}
                value={form.contactFirstName}
                onChange={(e) => setForm({ ...form, contactFirstName: e.currentTarget.value })}
                error={errors.firstName}
                required
                autoComplete="given-name"
              />
              <TextInput
                label={t('registration.start.lastName')}
                value={form.contactLastName}
                onChange={(e) => setForm({ ...form, contactLastName: e.currentTarget.value })}
                error={errors.lastName}
                required
                autoComplete="family-name"
              />
            </SimpleGrid>

            <TextInput
              label={t('registration.start.email')}
              type="email"
              value={form.contactEmail}
              onChange={(e) => setForm({ ...form, contactEmail: e.currentTarget.value })}
              error={errors.email}
              required
              autoComplete="email"
            />

            <SimpleGrid cols={2} spacing="sm">
              <TextInput
                label={
                  <>
                    {t('registration.start.phone')}{' '}
                    <Text component="span" c="dimmed" size="xs">{t('registration.start.optional')}</Text>
                  </>
                }
                type="tel"
                value={form.contactPhone}
                onChange={(e) => setForm({ ...form, contactPhone: e.currentTarget.value })}
                autoComplete="tel"
              />
              <Select
                label={
                  <>
                    {t('registration.start.warehouses')}{' '}
                    <Text component="span" c="dimmed" size="xs">{t('registration.start.optional')}</Text>
                  </>
                }
                placeholder={t('registration.start.warehousesChoose')}
                data={[
                  { value: '1', label: t('registration.start.warehousesRange1') },
                  { value: '2-5', label: t('registration.start.warehousesRange2to5') },
                  { value: '6-20', label: t('registration.start.warehousesRange6to20') },
                  { value: '20+', label: t('registration.start.warehousesRange20plus') },
                ]}
                value={form.warehouseRange || null}
                onChange={(v) => setForm({ ...form, warehouseRange: (v as FormState['warehouseRange']) ?? '' })}
              />
            </SimpleGrid>

            <Stack gap={6} p="sm" style={{ background: 'var(--mantine-color-gray-0)', borderRadius: 8 }}>
              {legalDocsQuery.isLoading && (
                <Text size="sm" c="dimmed">{t('shell.loading')}</Text>
              )}
              {legalDocsQuery.data?.map((doc) => (
                <Checkbox
                  key={doc.legalDocumentId}
                  checked={form.acceptedIds.has(doc.legalDocumentId)}
                  onChange={(e) => toggleDoc(doc.legalDocumentId, e.currentTarget.checked)}
                  label={
                    <Group gap={4} wrap="nowrap">
                      <Text size="sm" component="span">
                        {t('registration.start.agreeToTos', {
                          title: readableLegalDocTitle(doc.documentType, t),
                        })}
                      </Text>
                      <Anchor href="#" size="xs" c="dimmed">v{doc.version}</Anchor>
                    </Group>
                  }
                />
              ))}
              {errors.legal && (
                <Text size="xs" c="red">{errors.legal}</Text>
              )}
            </Stack>

            <Group
              gap="sm"
              p="xs"
              wrap="nowrap"
              style={{
                background: 'var(--mantine-color-gray-0)',
                border: '1px dashed var(--mantine-color-gray-3)',
                borderRadius: 8,
              }}
            >
              <IconShieldCheck size={20} color="var(--mantine-color-green-7)" />
              <Stack gap={0} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>{t('registration.start.verifiedHuman')}</Text>
                <Text size="xs" c="dimmed">{t('registration.start.turnstileProvider')}</Text>
              </Stack>
              <IconCheck size={18} color="var(--mantine-color-green-7)" />
            </Group>

            {submitError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {submitError}
              </Alert>
            )}

            <Button
              type="submit"
              color="invetrak"
              size="md"
              fullWidth
              loading={mutation.isPending}
              disabled={legalDocsQuery.isLoading}
            >
              {mutation.isPending
                ? t('registration.start.submitting')
                : t('registration.start.submit')}
            </Button>

            <Group justify="center" gap={6}>
              <Text size="sm" c="dimmed">{t('registration.start.haveAccount')}</Text>
              <Anchor
                size="sm"
                c="invetrak.7"
                component="button"
                type="button"
                onClick={() => void navigate({ to: '/login' })}
              >
                {t('registration.start.signIn')}
              </Anchor>
            </Group>
          </Stack>
        </form>
      </Stack>
    </RegistrationLayout>
  );
}

// Keep `legalDocLabelKey` referenced so future locales / doc types can be wired in
// without churning the export shape.
void legalDocLabelKey;
